import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { database } from '../firebaseConfig';
import { ref, push, onValue, update } from 'firebase/database';

function FormPage() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    mothersAdapted: 10,
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const leaderboardRef = ref(database, 'users');
    const unsubscribe = onValue(leaderboardRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const users = Object.entries(data).map(([id, user]) => ({
          id,
          ...user
        }));
        const sortedUsers = users.sort((a, b) => b.mothersAdapted - a.mothersAdapted);
        setLeaderboard(sortedUsers);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const incrementMothers = () => {
    setFormData(prev => ({
      ...prev,
      mothersAdapted: prev.mothersAdapted + 1
    }));
  };

  const decrementMothers = () => {
    setFormData(prev => ({
      ...prev,
      mothersAdapted: Math.max(1, prev.mothersAdapted - 1)
    }));
  };


  const sendAdoptNotification = async (phone,count ,id ) => {
    const req = await fetch("https://api.alloconnect.savemom.app/adopt_mom_success",{
      method:"POST",
      headers:{
        "Content-Type":"application/json"
      },
      body:JSON.stringify({
        count:count,
        id:id,
        phone:phone
      })
    })

    console.log(await req.json())
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const usersRef = ref(database, 'users');
      
      // Check if contact number already exists
      const existingUser = leaderboard.find(user => user.contact === formData.contact);
      
      if (existingUser) {
        // Update existing record by adding to the mother count
        const userRef = ref(database, `users/${existingUser.id}`);
        const newMotherCount = existingUser.mothersAdapted + parseInt(formData.mothersAdapted);
        
        await update(userRef, {
          name: formData.name,
          address: formData.address,
          mothersAdapted: newMotherCount,
          timestamp: Date.now()
        });

        await sendAdoptNotification(formData.contact,newMotherCount,existingUser.id);
        
        alert(`Updated! Your total mothers adopted: ${newMotherCount}`);
      } else {
        // Create new record
        const newUserRef = await push(usersRef, {
          ...formData,
          mothersAdapted: parseInt(formData.mothersAdapted),
          timestamp: Date.now()
        });

        await sendAdoptNotification(formData.contact, parseInt(formData.mothersAdapted), newUserRef.key);

      }
      
      setFormData({
        name: '',
        contact: '',
        mothersAdapted: 1,
        address: ''
      });
      
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalMothersAdapted = () => {
    return leaderboard.reduce((sum, user) => sum + (user.mothersAdapted || 0), 0);
  };

  return (
    <div className="App form-page">
      <Helmet>
        <title>Adopt a Mother - Make Your Contribution | Savemom</title>
        <meta name="description" content="Support expecting mothers through the Savemom Adopt a Mother program. Fill out the form to make your contribution and join our community of caring donors." />
        <meta property="og:title" content="Adopt a Mother - Make Your Contribution | Savemom" />
        <meta property="og:description" content="Support expecting mothers through the Savemom Adopt a Mother program. Fill out the form to make your contribution." />
        <meta property="og:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Adopt a Mother - Make Your Contribution | Savemom" />
        <meta name="twitter:description" content="Support expecting mothers through the Savemom Adopt a Mother program." />
        <meta name="twitter:image" content="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
        <link rel="icon" href="https://firebasestorage.googleapis.com/v0/b/savemom-starter.firebasestorage.app/o/systemimages%2Fdownload.png.1765516239066?alt=media&token=769e103e-9ce2-4c27-af5f-bff9b56bad9e" />
      </Helmet>
      {/* Header */}
      <header className="main-header">
        <div className="header-content">
          <div className="logo-section">
            <a href="/" className="logo-link">
              <div className="logo-icon">💚</div>
              <div className="logo-text">
                <h1>Savemom</h1>
                <span>Adopt a Mother Initiative</span>
              </div>
            </a>
          </div>
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-number">{leaderboard.length}</span>
              <span className="stat-label">Contributors</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">{getTotalMothersAdapted()}</span>
              <span className="stat-label">Mothers Adopted</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard">
        {/* Form Section */}
        <section className="form-section">
          {isSuccess && (
            <div className="success-message">
              <span>✅</span>
              Thank you! Your contribution has been registered successfully.
            </div>
          )}
          
          <div className="section-header">
            <h2>Adopt a Mother</h2>
            <p>Join our mission to save mothers</p>
          </div>
          
          <div className="form-container">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>
                  <span className="label-icon">👤</span>
                  Full Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>
                  <span className="label-icon">📱</span>
                  Contact Number
                </label>
                <input
                  type="tel"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  placeholder="XXXXX XXXXX"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>
                  <span className="label-icon">💝</span>
                  Mothers to Adopt
                </label>
                <div className="number-input-container">
                  <button 
                    type="button" 
                    className="number-btn minus-btn"
                    onClick={decrementMothers}
                    disabled={formData.mothersAdapted <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    name="mothersAdapted"
                    value={formData.mothersAdapted}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : parseInt(e.target.value);
                      setFormData(prev => ({ ...prev, mothersAdapted: value }));
                    }}
                    onBlur={(e) => {
                      const value = e.target.value === '' || parseInt(e.target.value) < 1 ? 1 : parseInt(e.target.value);
                      setFormData(prev => ({ ...prev, mothersAdapted: value }));
                    }}
                    min="1"
                    className="number-input"
                    required
                  />
                  <button 
                    type="button" 
                    className="number-btn plus-btn"
                    onClick={incrementMothers}
                  >
                    +
                  </button>
                </div>
              </div>
              
              <div className="form-group">
                <label>
                  <span className="label-icon">📍</span>
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter your complete address"
                  required
                />
              </div>
              
              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Submitting...
                  </>
                ) : (
                  <>
                    <span>🤝</span>
                    Submit & Make a Difference
                  </>
                )}
              </button>
            </form>
          </div>
        </section>

        {/* Leaderboard Section */}
        <section className="leaderboard-section">
          <div className="section-header">
            <h2>🏆 Leaderboard</h2>
            <p>Our amazing contributors</p>
          </div>
          <div className="leaderboard-container">
            {leaderboard.length > 0 ? (
              <>
                {/* Top 3 Cards */}
                <div className="top-three">
                  {leaderboard.slice(0, 3).map((user, index) => (
                    <div key={user.id} className={`top-card rank-${index + 1}`}>
                      <div className="top-card-medal">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </div>
                      <div className="top-card-avatar">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="top-card-info">
                        <span className="top-card-name" style={{color:"black"}}>{user.name}</span>
                        <span className="top-card-count">{user.mothersAdapted} mothers</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Rest of the leaderboard as table */}
                {leaderboard.length > 3 && (
                  <div className="leaderboard-table">
                    <div className="table-header">
                      <span className="table-col rank-col">Rank</span>
                      <span className="table-col name-col">Name</span>
                      <span className="table-col count-col">Mothers</span>
                    </div>
                    <div className="table-body">
                      {leaderboard.slice(3).map((user, index) => (
                        <div key={user.id} className="table-row">
                          <span className="table-col rank-col">#{index + 4}</span>
                          <span className="table-col name-col">
                            <div className="user-avatar-small">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {user.name}
                          </span>
                          <span className="table-col count-col">{user.mothersAdapted}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🎯</div>
                <p>No contributors yet.</p>
                <p>Be the first to make a difference!</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="main-footer">
        <div className="footer-content">
          <p>Powered by <strong>Savemom</strong> 💚</p>
          <p className="footer-tagline">Together, we can save lives</p>
        </div>
      </footer>
    </div>
  );
}

export default FormPage;
